import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDateTime } from '../../../config/appConfig';
import { useApprovals, type Approval } from '../../approvals/useData';
import GenericListWidget from './GenericListWidget';

function isEFormApproval(approval: Approval): boolean {
  return (
    approval.sourceModule === 'e_forms' ||
    approval.approvalType?.code === 'E_FORM_SUBMISSION'
  );
}

function parseDefinitionId(...candidates: unknown[]): number | null {
  for (const raw of candidates) {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (raw && typeof raw === 'object' && 'definitionId' in raw) {
      const nested = parseDefinitionId((raw as { definitionId?: unknown }).definitionId);
      if (nested != null) return nested;
    }
  }
  return null;
}

function approvalDefinitionId(approval: Approval): number | null {
  return parseDefinitionId(approval.payload?.definitionId);
}

const EformsApprovalPending: React.FC<{
  limit?: number;
  embedded?: boolean;
  definitionId?: number | string;
  settings?: Record<string, unknown>;
}> = ({ limit = 5, embedded, definitionId: definitionIdProp, settings }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const effectiveLimit = typeof settings?.limit === 'number' ? settings.limit : limit;
  const definitionId = parseDefinitionId(definitionIdProp, settings);
  const { data, isLoading } = useApprovals('mine', 1, '', { status: 'PENDING' });

  const items = useMemo(() => {
    const eforms = (data ?? []).filter(isEFormApproval);
    if (definitionId == null) return eforms;
    return eforms.filter((approval) => approvalDefinitionId(approval) === definitionId);
  }, [data, definitionId]);

  return (
    <GenericListWidget<Approval>
      title="E-Forms · Pending approval"
      viewAllPath="/approvals"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage={
        definitionId != null
          ? 'No pending approvals for this form'
          : 'No e-form approvals pending'
      }
      getItemKey={(item) => item.id}
      embedded={embedded}
      renderItem={(approval) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/approvals/${approval.id}`)}
          onKeyDown={(e) => e.key === 'Enter' && navigate(`/approvals/${approval.id}`)}
          className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
        >
          <div className="min-w-0">
            <p className={`text-sm font-medium truncate ${colors.text}`}>{approval.title}</p>
            <p className={`text-xs ${colors.textMuted}`}>
              {approval.approvalType?.name || 'E-Form submission'}
            </p>
          </div>
          <span className={`text-xs whitespace-nowrap ${colors.textMuted}`}>
            {formatDateTime(approval.submittedAt || approval.createdAt)}
          </span>
        </div>
      )}
    />
  );
};

export default EformsApprovalPending;
